import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { FlagWave } from '../components/FlagWave';
import { useAuth } from '../auth/AuthContext';
import { AppearanceMenu } from '../theme/Appearance';
import { NotificationBell } from '../components/NotificationBell';
import { AiLauncher } from '../components/AiLauncher';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  permission?: string;
  /** One line shown on hover and focus: what the page is for. */
  hint: string;
  /**
   * SkillXzen's own team only. A client company's admin holds the same
   * permissions for their own people, but already has Company Staff for that;
   * a second, platform-worded list of the same people is what made these
   * screens confusing.
   */
  platformOnly?: boolean;
}

const LEARNING: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: '▤',
    hint: 'Your progress at a glance, and where to pick up',
  },
  {
    to: '/catalog',
    label: 'Browse Catalog',
    icon: '◎',
    hint: 'Find and preview every course on SkillXzen',
  },
  // Staff roles without these see the page refused, so the link is not shown.
  {
    to: '/my/courses',
    label: 'My Learning',
    icon: '▶',
    permission: 'ENROLLMENT_VIEW',
    hint: 'The courses you are enrolled on',
  },
  {
    to: '/my/certificates',
    label: 'Certificates',
    icon: '✦',
    permission: 'CERTIFICATE_VIEW_OWN',
    hint: 'Certificates you have earned, ready to download',
  },
  {
    to: '/my/notifications',
    label: 'Notifications',
    icon: '✉',
    hint: 'Messages and reminders for you',
  },
];

/**
 * A client company's own portal. A manager sees it too, narrowed to their
 * remit. Worded from the client's side: it is their company and their staff.
 */
const ORGANIZATION: NavItem[] = [
  {
    to: '/org',
    label: 'Company Overview',
    icon: '⬡',
    permission: 'REPORT_VIEW',
    hint: 'Your company’s seats, teams and learning progress',
  },
  {
    to: '/org/employees',
    label: 'Company Staff',
    icon: '◍',
    permission: 'USER_VIEW',
    hint: 'The people in your company: invite, follow progress, free seats',
  },
  {
    to: '/org/assignments',
    label: 'Assigned Learning',
    icon: '⊞',
    permission: 'ASSIGNMENT_CREATE',
    hint: 'Assign courses to a person, team or department, with a due date',
  },
];

const ADMINISTRATION: NavItem[] = [
  // COURSE_UPDATE, not COURSE_VIEW: every role (learners included) can view
  // courses, but the authoring list refuses anyone who is not an author.
  {
    to: '/admin/courses',
    label: 'Courses',
    icon: '▦',
    permission: 'COURSE_UPDATE',
    hint: 'Build, edit and publish courses',
  },
  {
    to: '/admin/content',
    label: 'Content Library',
    icon: '❏',
    permission: 'CONTENT_VIEW',
    hint: 'Videos, documents and files used in lessons',
  },
  {
    to: '/admin/review',
    label: 'Review Queue',
    icon: '✓',
    permission: 'CONTENT_APPROVE',
    hint: 'Content and learner reviews waiting for approval',
  },
  {
    to: '/admin/users',
    label: 'All Learners & Staff',
    icon: '◍',
    permission: 'USER_VIEW',
    platformOnly: true,
    hint: 'Everyone who can sign in, across every client company',
  },
  {
    to: '/admin/customers',
    label: 'Clients',
    icon: '⬢',
    permission: 'CUSTOMER_VIEW',
    platformOnly: true,
    hint: 'The companies that use SkillXzen, each kept separate',
  },
  {
    to: '/admin/roles',
    label: 'Roles & Permissions',
    icon: '⚿',
    permission: 'ROLE_VIEW',
    hint: 'What each role can see and do',
  },
  {
    to: '/admin/reports',
    label: 'Reports & Analytics',
    icon: '◫',
    permission: 'PLATFORM_REPORT_VIEW',
    hint: 'Platform figures, trends and exports',
  },
  {
    to: '/admin/site',
    label: 'Site Content',
    icon: '◨',
    permission: 'BANNER_MANAGE',
    hint: 'Banners, pages and FAQs on the public site',
  },
  {
    to: '/admin/notifications',
    label: 'Notification Templates',
    icon: '✉',
    permission: 'NOTIFICATION_TEMPLATE_MANAGE',
    hint: 'The wording of emails and in-app messages',
  },
  {
    to: '/admin/ai',
    label: 'AI Assistant',
    icon: '✨',
    // Gated on viewing rather than managing, so a content manager who can
    // read the index state sees the entry even if they cannot change it.
    permission: 'AI_KNOWLEDGE_VIEW',
    hint: 'What Vagdevi knows, and how she answers',
  },
];

/** Promotions. Its own group so marketing staff find it without the admin list. */
const MARKETING: NavItem[] = [
  {
    to: '/admin/promo-codes',
    label: 'Promo Codes',
    icon: '⌗',
    permission: 'COUPON_VIEW',
    hint: 'Discount codes, campaigns and how they performed',
  },
];

/** Breadcrumbs for pages that have no sidebar entry of their own. */
const OFF_NAV: Array<{ prefix: string; title: string; section: string }> = [
  { prefix: '/checkout', title: 'Checkout', section: 'Learning' },
  { prefix: '/learn/', title: 'Course player', section: 'Learning' },
  { prefix: '/profile', title: 'Profile & Sessions', section: 'Account' },
];

const COLLAPSE_KEY = 'sx.nav.collapsed';

/** Which group a route belongs to, for the breadcrumb. */
const SECTIONS: Array<{ name: string; items: NavItem[] }> = [
  { name: 'Learning', items: LEARNING },
  { name: 'Company', items: ORGANIZATION },
  { name: 'Marketing', items: MARKETING },
  { name: 'Administration', items: ADMINISTRATION },
];

export function AppShell() {
  const { user, signOut, can } = useAuth();
  // Shown when the role may open it, and - for the platform-wide lists - only
  // to SkillXzen's own team (see NavItem.platformOnly).
  const isPlatformTeam = user?.tenantType === 'PLATFORM';
  const allowed = (item: NavItem): boolean =>
    (!item.permission || can(item.permission)) && (!item.platformOnly || isPlatformTeam);
  const location = useLocation();

  // Persisted, because a rail someone deliberately collapsed should stay
  // collapsed on the next page and the next visit.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* private mode: the choice just will not survive a reload */
    }
  }, [collapsed]);

  const toggleNav = useCallback(() => setCollapsed((value) => !value), []);

  const navRef = useRef<HTMLElement | null>(null);

  /**
   * Keep the current page's entry in sight.
   *
   * A Super Administrator's rail is longer than the window on anything
   * shorter than a desktop monitor, and the entries that overflow are the
   * administration ones at the bottom — so the account that needs them was
   * the one being shown a list that did not appear to contain the page it
   * was on. `nearest` does nothing when the link is already visible.
   */
  useEffect(() => {
    const active = navRef.current?.querySelector('.sidebar__link--active');
    active?.scrollIntoView({ block: 'nearest' });
  }, [location.pathname]);

  /**
   * Say which way the list continues.
   *
   * Without this a rail cut off at the fold looks like a rail that ends
   * there. The attribute drives an inset shadow, which stays with the box
   * rather than scrolling away with the content.
   */
  const [more, setMore] = useState<'none' | 'up' | 'down' | 'both'>('none');
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const update = () => {
      const top = nav.scrollTop > 2;
      // A pixel of slack: fractional layout heights mean the bottom is
      // rarely reached exactly, and without it the shadow never goes away.
      const bottom = nav.scrollTop + nav.clientHeight < nav.scrollHeight - 2;
      setMore(top && bottom ? 'both' : top ? 'up' : bottom ? 'down' : 'none');
    };

    update();
    nav.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    return () => {
      nav.removeEventListener('scroll', update);
      observer.disconnect();
    };
    // Re-measured when the rail collapses, which changes what fits.
  }, [collapsed]);

  const active =
    [...LEARNING, ...ORGANIZATION, ...MARKETING, ...ADMINISTRATION]
      .filter((item) => item.to !== '/org')
      .concat(ORGANIZATION.filter((item) => item.to === '/org'))
      .find((item) => location.pathname.startsWith(item.to)) ?? null;

  // Pages reached from inside another page rather than from the sidebar still
  // deserve a real breadcrumb.
  const offNav = OFF_NAV.find((page) => location.pathname.startsWith(page.prefix));
  const title = active?.label ?? offNav?.title ?? 'SkillXzen';
  const section =
    SECTIONS.find((group) => group.items.some((item) => item.to === active?.to))?.name ??
    offNav?.section ??
    'Account';

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'SX';

  return (
    <div className={`shell${collapsed ? ' shell--narrow' : ''}`}>
      <aside className="sidebar" id="sx-nav">
        <div className="sidebar__brand">
          {/* The on-dark lockup sits straight on the navy field, in the
              brand's own colours. Collapsed, the wordmark would not fit, so
              only the mark is kept — and the wave goes with it, because a
              36px mark rippling reads as a glitch rather than as cloth. */}
          <span className="sidebar__logo-plate">
            {collapsed ? (
              <Logo variant="mark" tone="onDark" />
            ) : (
              <FlagWave>
                <Logo variant="sidebar" tone="onDark" />
              </FlagWave>
            )}
          </span>
        </div>

        <nav className="sidebar__nav" ref={navRef} data-more={more}>
          <p className="sidebar__section">Learning</p>
          {LEARNING.filter(allowed).map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}

          {ORGANIZATION.some(allowed) ? (
            <>
              <p className="sidebar__section">Company</p>
              {ORGANIZATION.filter(allowed).map((item) => (
                <SidebarLink key={item.to} item={item} />
              ))}
            </>
          ) : null}

          {MARKETING.some(allowed) ? (
            <>
              <p className="sidebar__section">Marketing</p>
              {MARKETING.filter(allowed).map((item) => (
                <SidebarLink key={item.to} item={item} />
              ))}
            </>
          ) : null}

          {/* Running the platform is SkillXzen's job: a client company's admin
              works from the Company section and never sees this one, even
              where a role grants them one of its permissions. */}
          {isPlatformTeam && ADMINISTRATION.some(allowed) ? (
            <>
              <p className="sidebar__section">Administration</p>
              {ADMINISTRATION.filter(allowed).map((item) => (
                <SidebarLink key={item.to} item={item} />
              ))}
            </>
          ) : null}

          <p className="sidebar__section">Account</p>
          <SidebarLink
            item={{
              to: '/profile',
              label: 'Profile & Sessions',
              icon: '☰',
              hint: 'Your details, password and signed-in devices',
            }}
          />
        </nav>

        <div className="sidebar__foot">
          {collapsed ? `v${__APP_VERSION__}` : `SkillXzen · v${__APP_VERSION__}`}
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="topbar__left">
            <button
              type="button"
              className="navtoggle"
              onClick={toggleNav}
              aria-expanded={!collapsed}
              aria-controls="sx-nav"
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              <span aria-hidden="true">{collapsed ? '»' : '«'}</span>
            </button>

            {/* Collapsing the rail costs you the section heading it was
                showing, so the breadcrumb carries it instead. */}
            <nav className="crumbs" aria-label="Breadcrumb">
              <ol>
                <li>{section}</li>
                <li aria-current="page">{title}</li>
              </ol>
            </nav>
          </div>
          <div className="topbar__right">
            <NotificationBell />
            <AppearanceMenu />
            <AccountMenu
              initials={initials}
              avatarUrl={user?.avatarUrl ?? null}
              name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
              email={user?.email ?? ''}
              roles={user?.roles ?? []}
              onSignOut={() => void signOut()}
            />
          </div>
        </header>

        <div className="content">
          <Outlet />
        </div>
      </div>

      {/* Outside .content, so it is fixed to the viewport rather than
          scrolling away with the page, and present on every signed-in route
          because this shell wraps all of them. It renders nothing at all
          when the assistant is unavailable to this account. */}
      <AiLauncher />
    </div>
  );
}

/**
 * Identity in the top bar.
 *
 * The name, role and a sign-out button used to sit out in the open, which
 * repeated the account board on the dashboard almost word for word. Behind the
 * avatar they are still one click away and the bar stops competing with the
 * page.
 */
function AccountMenu({
  initials,
  avatarUrl,
  name,
  email,
  roles,
  onSignOut,
}: {
  initials: string;
  avatarUrl: string | null;
  name: string;
  email: string;
  roles: string[];
  onSignOut: () => void;
}) {
  /** The picture where there is one, initials where there is not. */
  const face = avatarUrl ? (
    <img className="avatar avatar--img" src={avatarUrl} alt="" />
  ) : (
    <span className="avatar" aria-hidden="true">
      {initials}
    </span>
  );
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="account" ref={wrap}>
      <button
        type="button"
        className="account__btn"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account: ${name}`}
        title={name}
      >
        {face}
      </button>

      {open ? (
        <div className="account__panel" role="menu">
          <div className="account__head">
            {face}
            <div className="account__who">
              <p className="account__name">{name}</p>
              <p className="account__mail">{email}</p>
            </div>
          </div>
          <div className="account__roles">
            {roles.map((role) => (
              <span className="pill pill--role" key={role}>
                {role}
              </span>
            ))}
          </div>
          <div className="account__links">
            <Link
              className="account__link"
              to="/profile"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Profile &amp; appearance
            </Link>
            <Link
              className="account__link"
              to="/my/notifications"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Notifications
            </Link>
            <button
              type="button"
              className="account__link account__link--out"
              role="menuitem"
              onClick={onSignOut}
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A sidebar entry with a one-line description beside it on hover or focus.
 *
 * The description is drawn in a portal at a fixed position rather than as a
 * CSS pseudo-element: the sidebar scrolls, and anything that tries to stick out
 * of a scrolling container is clipped at its edge. It names the page as well
 * as describing it, so it doubles as the label once the rail is collapsed to
 * icons. Devices without a hover pointer never see it — on a phone it would
 * appear on tap, over the page the tap is opening.
 */
function SidebarLink({ item }: { item: NavItem }) {
  const [tip, setTip] = useState<{ top: number; left: number } | null>(null);
  const hintId = `navhint-${item.to.replace(/[^a-z0-9]+/gi, '-')}`;

  const show = (event: SyntheticEvent<HTMLElement>) => {
    if (!window.matchMedia('(hover: hover)').matches && event.type === 'mouseenter') return;
    const box = event.currentTarget.getBoundingClientRect();
    setTip({ top: box.top + box.height / 2, left: box.right + 10 });
  };
  const hide = () => setTip(null);

  return (
    <>
      <NavLink
        to={item.to}
        aria-describedby={hintId}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={hide}
        className={({ isActive }) => `sidebar__link${isActive ? ' sidebar__link--active' : ''}`}
      >
        <span className="sidebar__icon" aria-hidden="true">
          {item.icon}
        </span>
        {/* Wrapped, not a bare text node: CSS cannot hide a text node, so the
            collapsed rail was still rendering every label and wrapping it into
            68px of width. */}
        <span className="sidebar__text">{item.label}</span>
        {/* For screen readers, always present; the visible panel is below. */}
        <span id={hintId} className="sr-only">
          {item.hint}
        </span>
      </NavLink>
      {tip
        ? createPortal(
            <div className="navtip" role="presentation" style={{ top: tip.top, left: tip.left }}>
              <strong className="navtip__title">{item.label}</strong>
              <span className="navtip__text">{item.hint}</span>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
